import { colors } from '@packages/core/styles';
import { Icon, LoadingCircle } from '@packages/ui/shared';
import { type SyntheticEvent, useCallback } from 'react';
import { createUseStyles } from 'react-jss';

import AssetPlaceholderImage from '../../../../assets/asset-placeholder@3x.png';

/** Props for the AttachmentPreview component. */
export type AttachmentPreviewProps = {
    /** A static file-type icon (e.g. SEPF SVG) to show instead of the generic file icon. */
    fileTypeIcon?: string;

    /** Whether the file is an image type. */
    isImage: boolean;

    /** Whether the asset is currently being processed. */
    isProcessing: boolean;

    /** The asset's display name (used as alt text). */
    name: string;

    /** Callback when the preview is clicked to view the asset. */
    onView: () => void;

    /** The thumbnail URL for image assets. */
    thumbnailUrl: string | undefined;
};

/**
 * Render the thumbnail preview or file icon for an attachment.
 *
 * @param props - the preview configuration
 * @returns the rendered attachment preview
 */
export function AttachmentPreview({
    fileTypeIcon,
    isImage,
    isProcessing,
    name,
    onView,
    thumbnailUrl,
}: AttachmentPreviewProps) {
    const classes = useStyles();
    const previewWrapperClass = isProcessing ? classes.previewWrapper : classes.previewWrapperHover;

    const handleClick = useCallback(() => {
        if (isProcessing) {
            return;
        }

        onView();
    }, [onView, isProcessing]);

    if (thumbnailUrl) {
        // Derive the opacity based on processing state
        const previewImageStyle = { opacity: isProcessing ? 0.5 : 1 };
        return (
            <div className={previewWrapperClass} onClick={handleClick}>
                <img
                    alt={name}
                    className={classes.filePreview}
                    onError={handleImageError}
                    src={thumbnailUrl}
                    style={previewImageStyle}
                />
                {isImage && isProcessing && (
                    <div className={classes.circleSpinner}>
                        <LoadingCircle />
                    </div>
                )}
            </div>
        );
    }

    if (fileTypeIcon) {
        return (
            <div className={previewWrapperClass} onClick={handleClick}>
                <img alt={name} className={classes.filePreview} onError={handleImageError} src={fileTypeIcon} />
            </div>
        );
    }

    return (
        <div className={previewWrapperClass} onClick={handleClick}>
            <div className={classes.fileIcon}>
                <Icon color="textDarkSecondary" name="file" size={38} />
            </div>
        </div>
    );
}
/**
 * Handle image load error by setting a placeholder image.
 *
 * @param event - the image load error event
 */
function handleImageError(event: SyntheticEvent<HTMLImageElement>) {
    const image = event.currentTarget;

    if (image.dataset.placeholderApplied === 'true') {
        return;
    }
    image.dataset.placeholderApplied = 'true';
    image.src = AssetPlaceholderImage;
}

const useStyles = createUseStyles({
    circleSpinner: {
        alignItems: 'center',
        display: 'flex',
        height: 64,
        justifyContent: 'center',
        left: 0,
        position: 'absolute',
        top: 0,
        width: 64,
    },
    fileIcon: {
        alignItems: 'center',
        backgroundColor: colors.grayOne,
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 4,
        display: 'flex',
        height: 64,
        justifyContent: 'center',
        width: 64,
    },
    filePreview: {
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 4,
        height: 64,
        objectFit: 'cover',
        width: 64,
    },
    previewWrapper: {
        position: 'relative',
    },
    previewWrapperHover: {
        position: 'relative',
        '&:hover': {
            cursor: 'pointer',
            opacity: 0.8,
        },
    },
});
